import React, { useEffect, useState } from "react";
import useLiveUpdates from "@/lib/useLiveUpdates";
import { Wifi, WifiOff } from "lucide-react";

/** Subtle top-of-page banner that surfaces only when the WebSocket is not 'open'.
 *  Subscribes to a noop topic so it gets the connection lifecycle.
 */
export default function ConnectionBanner() {
  const { status } = useLiveUpdates(["global"], () => {});
  const [recentReconnect, setRecentReconnect] = useState(false);

  // When the connection transitions back to 'open' after being reconnecting, show a brief 'restored' banner
  const prev = React.useRef(status);
  useEffect(() => {
    if (prev.current === "reconnecting" && status === "open") {
      setRecentReconnect(true);
      const t = setTimeout(() => setRecentReconnect(false), 2500);
      return () => clearTimeout(t);
    }
    prev.current = status;
  }, [status]);

  if (status === "open" && !recentReconnect) return null;

  let cls = "sev-warning";
  let label = "Reconnecting to live updates…";
  let Icon = WifiOff;
  if (status === "open" && recentReconnect) {
    cls = "sev-ok";
    label = "Live updates restored";
    Icon = Wifi;
  } else if (status === "closed") {
    cls = "sev-critical";
    label = "Live updates offline";
    Icon = WifiOff;
  } else if (status === "connecting") {
    cls = "sev-info";
    label = "Connecting to live updates…";
  }

  return (
    <div
      className={`fixed top-3 left-1/2 -translate-x-1/2 z-50 ${cls} px-3 py-1.5 flex items-center gap-2 font-mono text-[11px]`}
      data-testid="ws-connection-banner"
    >
      <Icon size={12} className={status === "reconnecting" || status === "connecting" ? "animate-pulse" : ""} />
      <span>{label}</span>
    </div>
  );
}
